import React, { useState, useEffect, useCallback } from 'react';
import { UserPlus, UserMinus, Loader2 } from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { useFollows, useFollowMutation } from '../hooks/useFollows';

interface FollowButtonProps {
  pubkey: string;
  size?: 'sm' | 'md';
  className?: string;
}

export default function FollowButton({ pubkey, size = 'sm', className = '' }: FollowButtonProps) {
  const { user } = useAppContext();
  const { follows, loading: followsLoading } = useFollows(user?.pubkey);
  const { mutate, ready } = useFollowMutation();
  const [isFollowing, setIsFollowing] = useState(false);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    setIsFollowing(follows.includes(pubkey));
  }, [follows, pubkey]);

  // Don't show for own profile or if not logged in (AFTER all hooks)
  if (!user || user.pubkey === pubkey) return null;

  const handleToggle = async (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // If still loading, give user feedback instead of silently doing nothing
    if (!ready) {
      // On mobile, tapping a "disabled" button is confusing — let them retry
      // after a brief moment. The ready gate will catch up.
      return;
    }

    if (toggling) return;
    setToggling(true);

    try {
      const success = await mutate(
        isFollowing ? 'unfollow' : 'follow',
        pubkey,
      );
      if (success) {
        setIsFollowing(!isFollowing);
      }
    } catch (err) {
      console.error('Follow toggle failed:', err);
    } finally {
      setToggling(false);
    }
  };

  const isSm = size === 'sm';

  // Show a loading skeleton while follows are loading, but don't hide the button entirely.
  // Hiding the button on mobile (returning null) causes layout shift and confusion.
  const isLoading = followsLoading && !ready;

  return (
    <button
      onClick={handleToggle}
      disabled={toggling}
      className={`inline-flex items-center justify-center gap-1 rounded-full font-medium transition-all shrink-0 active:scale-95 select-none touch-manipulation ${
        isLoading
          ? `border border-border text-muted-foreground/50 ${isSm ? 'px-3 py-1.5 text-[11px] min-h-[32px]' : 'px-4 py-2 text-xs min-h-[36px]'}`
          : toggling
            ? `border border-border text-muted-foreground ${isSm ? 'px-3 py-1.5 text-[11px] min-h-[32px]' : 'px-4 py-2 text-xs min-h-[36px]'}`
            : isFollowing
              ? `border border-border text-muted-foreground hover:border-red-500/50 hover:text-red-500 active:bg-red-500/10 ${isSm ? 'px-3 py-1.5 text-[11px] min-h-[32px]' : 'px-4 py-2 text-xs min-h-[36px]'}`
              : `bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/80 ${isSm ? 'px-3 py-1.5 text-[11px] min-h-[32px]' : 'px-4 py-2 text-xs min-h-[36px]'}`
      } ${toggling ? 'opacity-70' : ''} ${className}`}
    >
      {toggling || isLoading ? (
        <Loader2 className={`animate-spin ${isSm ? 'w-3 h-3' : 'w-3.5 h-3.5'}`} />
      ) : isFollowing ? (
        <UserMinus className={isSm ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
      ) : (
        <UserPlus className={isSm ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
      )}
      {isLoading ? '' : isFollowing ? 'Following' : 'Follow'}
    </button>
  );
}

